"use client";
import { useState } from "react";
import Image from "next/image";
import { Gallery } from "../Gallery";
import { RichtextRenderer } from "@/components/atoms/rich-text-render";import {
  BannerDatasource,
  CrmBannerDatasource,
  CrmSceneImage,
} from "@/types/generated";
interface HotSpringBannerProps {
  hotSpringBanner: BannerDatasource;
}
// Displays a dynamic banner section for Hot Spring pages with image gallery and localized text

export const HotSpringBanner = ({ hotSpringBanner }: HotSpringBannerProps) => {
  const [currentBanner] = useState(0);
  const { crm_banner_images, banner_images } = hotSpringBanner || {};

  // Extract document IDs from banner image references
  const bannerImageIds = banner_images.map(
    (scene: CrmSceneImage) => scene?.documentId,
  );

  const listBanner = bannerImageIds
    .map((id: string) =>
      crm_banner_images.find(
        (scene: CrmBannerDatasource | null) =>
          scene !== null && scene.documentId === id,
      ),
    )
    .filter(Boolean);

  const bannerImages = listBanner?.map((x: CrmBannerDatasource | null) => ({
    cloudflare_url: x?.banner_desktop_image?.cloudflare_url ?? "",
    url: x?.banner_desktop_image?.url ?? "",
    name: x?.banner_desktop_image?.name ?? "",
    alt: x?.banner_desktop_image?.alternativeText ?? "",
    caption: x?.banner_desktop_image?.caption ?? "",
  }));
  const bannerTitle = listBanner?.filter(
    (x: CrmBannerDatasource | null) =>
      x?.banner_top_text && x?.banner_bottom_text,
  );

  return (
    <div className={`relative min-h-[375px] w-full md:min-h-[720px]`}>
      <Gallery
        dotClassName="md:p-1.5"
        isOnlyPageNumber
        activeDotClassName="max-md:bg-white"
        inactiveDotClassName="max-md:bg-gray-400"
        navigationClassName="md:hidden text-[var(--text-base-primary)]"
        paginationClassName="md:bottom-25 bottom-11"
        containerClassName="h-full w-full"
      >
        {bannerImages?.map(
          (
            image: {
              cloudflare_url?: string;
              url?: string;
              name?: string;
              alt?: string;
              caption?: string;
            } | null,
            index: number,
          ) => {
            const hotSpringBannerImageKey = `hot-spring-banner-image-${index}`;
            const isFirstImage = index < 2;
            return (
              <div
                key={hotSpringBannerImageKey}
                className={`h-full min-h-[375px] w-full md:min-h-[720px]`}
              >
                <Image
                  src={image?.cloudflare_url || image?.url || ""}
                  alt={image?.alt || ""}
                  aria-label={image?.caption || ""}
                  fill
                  priority={isFirstImage}
                  className="object-cover"
                />
              </div>
            );
          },
        )}
      </Gallery>

      {bannerTitle && (
        <div className="pointer-events-none absolute -bottom-65 z-10 min-h-[293px] w-full sm:-bottom-52 sm:min-h-[250px] md:-bottom-80 md:-mt-30 md:min-h-[350px] md:px-5 lg:-bottom-70 xl:-bottom-75">
          <div className="mx-auto w-11/12 rounded-xl border border-gray-300 bg-white px-4 py-5 shadow-xl md:w-6/7 md:p-6 xl:w-4/7">
            <h2 className="mb-2 line-clamp-3 text-center text-lg sm:text-2xl md:mb-3 md:text-[36px]">
              <RichtextRenderer
                customStyle={{
                  "": {
                    color: "var(--text-base-primary)",
                    fontWeight: 600,
                  },
                }}
                content={bannerTitle?.[currentBanner]?.banner_top_text ?? ""}
              />
            </h2>
            <div className="line-clamp-6 text-center text-sm leading-relaxed md:text-xl">
              <RichtextRenderer
                customStyle={{
                  "": {
                    color: "var(--text-gray-stronger)",
                  },
                }}
                content={bannerTitle[currentBanner]?.banner_bottom_text || ""}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
